/**
 * MFAChallengePage - MFA verification challenge for authentication
 *
 * This page is opened when MFA is required during login.
 * The user verifies their identity using TOTP or email OTP.
 */

import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getApiUrl } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Shield, Mail, AlertTriangle, ArrowLeft } from 'lucide-react'

// API_URL is computed dynamically via getApiUrl() for org-scoped subdomains

export function MFAChallengePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const sessionId = searchParams.get('session_id')
  const methodsParam = searchParams.get('methods') || ''
  const redirectTo = searchParams.get('redirect_to') || '/'

  // Parse available methods from query params
  const availableMethods = methodsParam.split(',').filter(Boolean)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(
    availableMethods.length === 1 ? availableMethods[0] : null
  )

  // TOTP state
  const [totpCode, setTotpCode] = useState('')

  // Email OTP state
  const [emailSent, setEmailSent] = useState(false)
  const [emailCode, setEmailCode] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  // Validate session_id is present
  useEffect(() => {
    if (!sessionId) {
      setError('Invalid MFA challenge - missing session')
    }
  }, [sessionId])

  // Auto-submit when TOTP code reaches 6 digits
  useEffect(() => {
    if (selectedMethod === 'totp' && totpCode.length === 6 && !loading) {
      handleVerify()
    }
  }, [totpCode, selectedMethod])

  async function handleVerify() {
    if (!sessionId) return

    const code = selectedMethod === 'totp' ? totpCode : emailCode
    if (!code || code.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${getApiUrl()}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          method: selectedMethod,
          code: code,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.detail || 'Verification failed')
        // Clear the code on failure
        if (selectedMethod === 'totp') {
          setTotpCode('')
        } else {
          setEmailCode('')
        }
        return
      }

      // Success - redirect to original destination
      window.location.href = redirectTo
    } catch (err) {
      setError('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSendEmailOtp() {
    if (!sessionId) return

    setSendingEmail(true)
    setError(null)

    try {
      const response = await fetch(`${getApiUrl()}/api/auth/mfa/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
        }),
      })

      if (response.ok) {
        setEmailSent(true)
      } else {
        const data = await response.json()
        setError(data.detail || 'Failed to send email code')
      }
    } catch (err) {
      setError('Failed to send email code')
    } finally {
      setSendingEmail(false)
    }
  }

  function handleBackToMethods() {
    setSelectedMethod(null)
    setTotpCode('')
    setEmailCode('')
    setEmailSent(false)
    setError(null)
  }

  // Error state - no session
  if (!sessionId) {
    return (
      <AuthLayout>
        <Card className="p-8">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h2 className="text-center text-lg font-semibold text-[var(--text-primary)]">
            Invalid MFA Challenge
          </h2>
          <p className="mt-2 text-center text-sm text-[var(--text-secondary)]">
            This verification request is invalid or has expired.
          </p>
          <div className="mt-6">
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
              Return to Login
            </Button>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  // No methods available
  if (availableMethods.length === 0) {
    return (
      <AuthLayout>
        <Card className="p-8">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
              <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <h2 className="text-center text-lg font-semibold text-[var(--text-primary)]">
            No MFA Methods Available
          </h2>
          <p className="mt-2 text-center text-sm text-[var(--text-secondary)]">
            You don't have any MFA methods configured. Please contact support.
          </p>
          <div className="mt-6">
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
              Return to Login
            </Button>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card className="overflow-hidden">
        {/* Header */}
        <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="font-semibold text-[var(--text-primary)]">
                Two-Factor Authentication
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                Verify your identity to continue
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Error message */}
          {error && (
            <div className="mb-4 rounded-lg bg-[var(--error-muted)]/30 p-3 text-sm text-[var(--error)]">
              {error}
            </div>
          )}

          {/* Method Selection */}
          {!selectedMethod && availableMethods.length > 1 && (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-secondary)]">
                Choose a verification method:
              </p>

              {availableMethods.includes('totp') && (
                <button
                  onClick={() => setSelectedMethod('totp')}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
                >
                  <Shield className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">
                      Authenticator App
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Use your authenticator app
                    </p>
                  </div>
                </button>
              )}

              {availableMethods.includes('email') && (
                <button
                  onClick={() => setSelectedMethod('email')}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
                >
                  <Mail className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">Email Code</p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Send a verification code to your email
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* TOTP Verification */}
          {selectedMethod === 'totp' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-[var(--text-primary)]">
                  Authenticator App
                </span>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">
                Enter the 6-digit code from your authenticator app
              </p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
              />
              <Button
                className="w-full"
                onClick={handleVerify}
                disabled={loading || totpCode.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              {availableMethods.length > 1 && (
                <button
                  onClick={handleBackToMethods}
                  className="flex w-full items-center justify-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Try a different method
                </button>
              )}
            </div>
          )}

          {/* Email OTP Verification */}
          {selectedMethod === 'email' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-[var(--text-primary)]">
                  Email Verification
                </span>
              </div>

              {!emailSent ? (
                <>
                  <p className="text-sm text-[var(--text-secondary)]">
                    We'll send a verification code to your email address.
                  </p>
                  <Button
                    className="w-full"
                    onClick={handleSendEmailOtp}
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? 'Sending...' : 'Send Code to Email'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-[var(--text-secondary)]">
                    We sent a code to your email. Enter it below:
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    autoFocus
                  />
                  <Button
                    className="w-full"
                    onClick={handleVerify}
                    disabled={loading || emailCode.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </Button>
                  <button
                    onClick={handleSendEmailOtp}
                    disabled={sendingEmail}
                    className="w-full text-sm text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                  >
                    {sendingEmail ? 'Sending...' : 'Resend code'}
                  </button>
                </>
              )}

              {availableMethods.length > 1 && (
                <button
                  onClick={handleBackToMethods}
                  className="flex w-full items-center justify-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Try a different method
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--border-subtle)] p-4">
          <button
            onClick={() => navigate('/login')}
            className="flex w-full items-center justify-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Cancel and return to login
          </button>
        </div>
      </Card>
    </AuthLayout>
  )
}
